import { Badge, Button, Text } from "@microbeta/ui";
import type React from "react";
import { useTranslation } from "react-i18next";
import { MdDelete, MdEdit, MdImage } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import type { ImageItem } from "../../../services/imageUtils";
import { ResponsiveImage } from "../ResponsiveImage/ResponsiveImage";
import styles from "./AdminContentCard.module.scss";

export interface AdminContentCardProps {
  id: string;
  name: string;
  updatedAt: string;
  image?: string | ImageItem;
  typeLabel: string;
  editPath: string;
  viewPath: string;
  showActions?: boolean;
  onDelete?: (id: string) => void;
  icon?: React.ReactNode;
  extraMeta?: React.ReactNode;
  className?: string;
}

export const AdminContentCard: React.FC<AdminContentCardProps> = ({
  id,
  name,
  updatedAt,
  image,
  typeLabel,
  editPath,
  viewPath,
  showActions = false,
  onDelete,
  icon,
  extraMeta,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(id);
    }
  };

  const renderImage = () => {
    if (typeof image === "object" && image !== null) {
      // Check if it's the new ImageItem format (has 'original' property)
      if ("original" in image) {
        return (
          <ResponsiveImage
            variants={image as ImageItem}
            alt={name}
            className={styles.thumbnail}
            loading="lazy"
            sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 300px"
            size="small"
          />
        );
      }
    }
    if (typeof image === "string") {
      // String URL
      return (
        <img
          src={image}
          alt={name}
          className={styles.thumbnail}
          loading="lazy"
        />
      );
    }
    // No image - show placeholder with icon
    return <div className={styles.imagePlaceholder}>{icon || <MdImage />}</div>;
  };

  const handleActionClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(editPath);
  };

  return (
    <div className={`${styles.cardContainer} ${className}`}>
      <Link to={viewPath} className={`${styles.card} ${styles.clickableCard}`}>
        <div className={styles.imageContainer}>
          {renderImage()}

          {showActions && (
            <div
              className={styles.overlayActions}
              onClick={handleActionClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleActionClick(e);
                }
              }}
              role="toolbar"
              aria-label="Card actions"
            >
              <button
                type="button"
                className={styles.overlayActionButton}
                title={t("common.edit")}
                onClick={handleEditClick}
              >
                <MdEdit />
              </button>
              {onDelete && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleDeleteClick}
                  className={styles.overlayDeleteButton}
                  title={t("common.delete")}
                >
                  <MdDelete />
                </Button>
              )}
            </div>
          )}
        </div>

        <div className={styles.cardContent}>
          <div className={styles.cardHeader}>
            <Text variant="large" className={styles.cardTitle}>
              {name}
            </Text>
          </div>
          {typeLabel && (
            <Badge className={styles.typeLabel} variant="primary" size="small">
              {typeLabel}
            </Badge>
          )}

          <Text variant="small" className={styles.cardMeta}>
            {t("adminContentCard.lastUpdated")}:{" "}
            {new Date(updatedAt).toLocaleDateString()}
          </Text>

          {extraMeta && <div className={styles.extraMeta}>{extraMeta}</div>}
        </div>
      </Link>
    </div>
  );
};
